// Turning what a date picker shows into the date we book.
//
// Split out of TemplatePage so it can be tested directly: this is the function
// that decides which nights a guest pays for, and the bug it used to carry was
// invisible from the outside, because it only appeared for visitors in another
// timezone.

/**
 * Parse a date-picker value (native `YYYY-MM-DD`, or the theme picker's
 * "24 July, 2026") into a calendar date.
 *
 * A calendar date carries no time and no zone, so it must never be routed
 * through UTC. `toISOString()` on a locally-parsed date returns the day before
 * for anyone east of Greenwich; reading local fields off a UTC-parsed date
 * returns the day before for anyone west of it, which is most of our guests. So
 * each input form is handled in the one way that cannot shift it.
 *
 * The same bug class was already found and fixed in `parseDate` and in
 * `stayDate`; it just never reached here.
 */
export function toIsoDate(value: string): string | null {
  if (!value) return null;
  const raw = value.trim();

  // Already a calendar date, which is what a native <input type="date"> gives.
  // Returned untouched: building a Date from it and formatting it back is the
  // whole round trip the bug lived in.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // The theme picker's display form. Parsed as local midnight, so it is read
  // back out of the LOCAL fields to match.
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return localCalendarDate(parsed);

  const m = raw.match(/(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})/);
  if (m) {
    const d = new Date(`${m[2]} ${m[1]}, ${m[3]}`);
    if (!Number.isNaN(d.getTime())) return localCalendarDate(d);
  }
  return null;
}

/** The calendar date a Date represents where the visitor is standing. */
function localCalendarDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}
